import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Heading,
  Text,
  Avatar,
  Spinner,
  Button,
  useToast,
  VStack,
  Divider,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { StarRating, InteractiveStarRating } from "../components/StarRating";
import { ChatState } from "../Context/ChatProvider";
import CommentSection from "../components/CommentSection";

const VendorProfilePage = () => {
  const { vendorId } = useParams();
  const location = useLocation();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRating, setUserRating] = useState(0);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = ChatState();

  // Fetch vendor data based on vendorId
  useEffect(() => {
    const fetchVendor = async () => {
      try {
        const response = await axios.get(`/api/user/${vendorId}`);
        setVendor(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching vendor data: ", error);
        setLoading(false);
      }
    };

    fetchVendor();
  }, [vendorId]);

  // Fetch user's existing rating for this vendor
  useEffect(() => {
    const fetchUserRating = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) return;

        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const { data } = await axios.get(`/api/rating/${vendorId}`, config);

        if (data.rating) {
          setUserRating(data.rating);
        }
      } catch (error) {
        console.error("Error fetching user rating: ", error);
      }
    };

    if (vendorId && user) {
      fetchUserRating();
    }
  }, [vendorId, user]);

  if (loading) {
    return <Spinner size="xl" color="orange.500" />;
  }

  if (!vendor) {
    return <Text>No vendor data found.</Text>;
  }

  // Retrieve category from location state
  const category = location.state?.category;

  // Handle back button click
  const handleBackButtonClick = () => {
    if (category) {
      navigate(`/category/${category}`);
    } else {
      navigate("/");
    }
  };

  // Handle 'Chat Now' button click
  const handleChatNowClick = async () => {
    try {
      // Check if user is logged in
      const token = localStorage.getItem("authToken");

      if (!token) {
        // Redirect to login if not authenticated
        navigate("/login");
        return;
      }

      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`, // Pass the user's token for authentication
        },
      };

      // Send a POST request to create a chat with the vendor
      const { data } = await axios.post(
        "/api/chat",
        { userId: vendor._id }, // Use vendor's _id to start the chat
        config
      );

      // Navigate to the chats page and pass the chat ID as state
      navigate("/chats", { state: { chatId: data._id } });
    } catch (error) {
      console.error("Error starting chat: ", error);
    }
  };

  // Handle rating submission
  const handleRatingChange = async (rating) => {
    setIsSubmittingRating(true);
    try {
      const token = localStorage.getItem("authToken");

      if (!token) {
        toast({
          title: "Please login to rate",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        navigate("/login");
        return;
      }

      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const { data } = await axios.post(
        "/api/rating",
        { vendorId: vendor._id, rating: rating },
        config
      );

      setUserRating(rating);

      // Update vendor's rating display
      setVendor({
        ...vendor,
        averageRating: data.vendorRating.averageRating,
        totalRatings: data.vendorRating.totalRatings,
      });

      toast({
        title: "Rating submitted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error submitting rating",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingRating(false);
    }
  };

  // Handle rating removal
  const handleRemoveRating = async () => {
    setIsSubmittingRating(true);
    try {
      const token = localStorage.getItem("authToken");

      if (!token) {
        toast({
          title: "Please login",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        navigate("/login");
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const { data } = await axios.delete(`/api/rating/${vendor._id}`, config);

      setUserRating(0);

      // Update vendor's rating display
      setVendor({
        ...vendor,
        averageRating: data.vendorRating.averageRating,
        totalRatings: data.vendorRating.totalRatings,
      });

      toast({
        title: "Rating removed successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error removing rating",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingRating(false);
    }
  };

  return (
    <Box p={8} textAlign="center">
      {/* Back Button with Arrow and Text, positioned on the left side */}
      <Button
        leftIcon={<ArrowBackIcon />}
        colorScheme="blue"
        variant="outline"
        onClick={handleBackButtonClick}
        mb={4}
        position="absolute"
        left={8}
      >
        Back
      </Button>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        borderWidth="1px"
        borderRadius="lg"
        boxShadow="lg"
        maxW="600px"
        margin="auto"
        mt={12}
        p={6}
      >
        <Avatar size="2xl" name={vendor.name} src={vendor.pic} mt={4} mb={4} />
        <Heading fontSize="3xl">{vendor.name}</Heading>
        <Text fontSize="lg" color="gray.500">
          {vendor.email}
        </Text>

        {/* Display vendor's average rating */}
        <Box mt={3}>
          <StarRating
            rating={vendor.averageRating || 0}
            totalRatings={vendor.totalRatings || 0}
            size={20}
          />
        </Box>

        <Text mt={2} color="orange.600" fontWeight="semibold">
          {vendor.categories && vendor.categories.length > 0
            ? vendor.categories.join(", ")
            : "No categories listed"}
        </Text>
        <Text mt={4} fontSize="lg">
          {vendor.description}
        </Text>

        {/* Chat Now Button */}
        <Button
          colorScheme="orange"
          mt={4}
          onClick={handleChatNowClick}
          width="100%"
        >
          Chat Now
        </Button>

        {/* Rating Section */}
        {user && user._id !== vendor._id && user.role !== "admin" && (
          <>
            <Divider my={6} />
            <VStack spacing={3} width="100%">
              <Heading fontSize="xl">Rate this vendor</Heading>
              <InteractiveStarRating
                onRatingChange={handleRatingChange}
                currentRating={userRating}
                size={36}
              />
              {userRating > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={handleRemoveRating}
                  isLoading={isSubmittingRating}
                >
                  Remove Rating
                </Button>
              )}
              {isSubmittingRating && <Spinner size="sm" color="orange.500" />}
            </VStack>
          </>
        )}

        {/* Comment Section */}
        <CommentSection vendorId={vendor._id} />
      </Box>
    </Box>
  );
};

export default VendorProfilePage;
